import type { PanderrTemplateDefinition } from "../types";

export const HOSPITALITY_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "hospitality-modern",
  name: "Hospitality Modern",
  description:
    "A welcoming, image-forward website for vacation rentals, boutique stays, inns, lodges, cabins, and hospitality businesses.",
  category: "hospitality",

  navigation: [
    { label: "Stay", href: "/stay/" },
    { label: "Amenities", href: "/amenities/" },
    { label: "Area Guide", href: "/area-guide/" },
    { label: "Gallery", href: "/gallery/" },
    { label: "Contact", href: "/contact/" },
  ],

  headerCta: {
    label: "Plan Your Stay",
    href: "/contact/",
    variant: "primary",
  },

  footer: {
    description:
      "A comfortable place to stay, thoughtfully designed for memorable getaways.",
    columns: [
      {
        heading: "Explore",
        links: [
          { label: "Stay", href: "/stay/" },
          { label: "Amenities", href: "/amenities/" },
          { label: "Gallery", href: "/gallery/" },
        ],
      },
      {
        heading: "Your Visit",
        links: [
          { label: "Area Guide", href: "/area-guide/" },
          { label: "Plan Your Stay", href: "/contact/" },
          { label: "Contact", href: "/contact/" },
        ],
      },
    ],
  },

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Your getaway starts here",
      heading: "A beautiful place to slow down, settle in, and stay awhile.",
      body: "Thoughtful accommodations, comfortable spaces, and a memorable stay close to everything you came to experience.",
      images: [
        {
          src: "/studio/assets/stock/images/spacejoy-9M66C_w_ToM-unsplash.jpg",
          alt: "Welcoming guest accommodation",
          role: "hero",
        },
      ],
      buttons: [
        {
          label: "Explore Your Stay",
          href: "/stay/",
          variant: "primary",
        },
        {
          label: "View Gallery",
          href: "/gallery/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
      eyebrow: "Comfortable. Convenient. Memorable.",
      heading: "Everything you need for an easier getaway.",
      body: "Relax in thoughtfully prepared spaces with the comforts, amenities, and local access that make your stay feel effortless.",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "Make yourself at home",
      heading: "Designed around the way you want to stay.",
      body: "Whether you're planning a weekend escape, family trip, romantic getaway, or longer visit, enjoy spaces designed for comfort and connection.",
      services: [
        {
          title: "Beautiful Accommodations",
          description:
            "Comfortable, thoughtfully designed spaces with room to unwind and recharge.",
          image: {
            src: "/studio/assets/stock/images/spacejoy-IH7wPsjwomc-unsplash.jpg",
            alt: "Comfortable guest accommodation",
            role: "service",
          },
          href: "/stay/",
        },
        {
          title: "Thoughtful Amenities",
          description:
            "The practical comforts and special touches that make your stay easier and more enjoyable.",
          image: {
            src: "/studio/assets/stock/images/spacejoy-h2_3dL9yLpU-unsplash.jpg",
            alt: "Thoughtfully designed hospitality interior",
            role: "service",
          },
          href: "/amenities/",
        },
        {
          title: "A Great Location",
          description:
            "Stay close to local dining, activities, attractions, and the places you came to explore.",
          image: {
            src: "/studio/assets/stock/images/spacejoy-YI2YkyaREHk-unsplash.jpg",
            alt: "Relaxing guest space",
            role: "service",
          },
          href: "/area-guide/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "More than a place to sleep",
      heading: "A stay designed to feel easy from the moment you arrive.",
      body: "We believe the best hospitality combines comfort, thoughtful details, and a sense of place. Settle in, explore the area, and enjoy a stay that feels welcoming from start to finish.",
      images: [
        {
          src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
          alt: "Comfortable hospitality interior",
          role: "about",
        },
      ],
      buttons: [
        {
          label: "Discover the Experience",
          href: "/amenities/",
          variant: "text",
        },
      ],
    },

    {
      id: "gallery",
      type: "gallery",
      eyebrow: "Take a look around",
      heading: "Picture yourself here.",
      body: "Explore the spaces, details, and atmosphere that make this stay special.",
      images: [
        {
          src: "/studio/assets/stock/images/spacejoy-9M66C_w_ToM-unsplash.jpg",
          alt: "Hospitality accommodation",
          role: "card",
        },
        {
          src: "/studio/assets/stock/images/spacejoy-IH7wPsjwomc-unsplash.jpg",
          alt: "Guest interior",
          role: "card",
        },
        {
          src: "/studio/assets/stock/images/spacejoy-YI2YkyaREHk-unsplash.jpg",
          alt: "Relaxing guest space",
          role: "card",
        },
      ],
    },

    {
      id: "testimonials",
      type: "testimonials",
      eyebrow: "Guest experiences",
      heading: "The kind of stay guests want to come back to.",
      testimonials: [
        {
          quote:
            "Beautiful, comfortable, and exactly the relaxing getaway we were hoping for.",
          name: "Recent Guest",
          detail: "Weekend stay",
        },
        {
          quote:
            "The space was welcoming, thoughtfully prepared, and close to everything we wanted to do.",
          name: "Recent Guest",
          detail: "Vacation stay",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Ready for a getaway?",
      heading: "Start planning your stay.",
      body: "Tell us when you're visiting and what you're looking for, and we'll help you take the next step.",
      buttons: [
        {
          label: "Plan Your Stay",
          href: "/contact/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "stay",
      name: "Stay",
      title: "Stay",
      slug: "/stay/",
      sections: [
        {
          id: "stay-hero",
          type: "hero",
          eyebrow: "Your home away from home",
          heading: "Comfortable spaces made for relaxing stays.",
          body: "Explore accommodations designed for comfort, convenience, and time well spent together.",
          images: [
            {
              src: "/studio/assets/stock/images/spacejoy-IH7wPsjwomc-unsplash.jpg",
              alt: "Comfortable guest accommodation",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Plan Your Stay",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
        {
          id: "stay-options",
          type: "services",
          eyebrow: "Choose your stay",
          heading: "Find the space that fits your visit.",
          body: "Use these sections for rooms, suites, cabins, rental homes, cottages, or other accommodation options.",
          services: [
            {
              title: "Signature Stay",
              description:
                "A comfortable accommodation with thoughtful details and everything you need to settle in.",
              image: {
                src: "/studio/assets/stock/images/spacejoy-9M66C_w_ToM-unsplash.jpg",
                alt: "Signature accommodation",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Spacious Stay",
              description:
                "More room for families, groups, longer visits, or guests who simply want extra space.",
              image: {
                src: "/studio/assets/stock/images/spacejoy-h2_3dL9yLpU-unsplash.jpg",
                alt: "Spacious accommodation",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Premium Stay",
              description:
                "An upgraded option featuring extra comforts, beautiful spaces, and memorable details.",
              image: {
                src: "/studio/assets/stock/images/spacejoy-YI2YkyaREHk-unsplash.jpg",
                alt: "Premium accommodation",
                role: "service",
              },
              href: "/contact/",
            },
          ],
        },
        {
          id: "stay-faq",
          type: "faq",
          eyebrow: "Before you arrive",
          heading: "Helpful information for your stay.",
          faqs: [
            {
              question: "What time is check-in and check-out?",
              answer: "Add your property's check-in and check-out times here.",
            },
            {
              question: "How do reservations work?",
              answer:
                "Use this space to explain your booking, deposit, cancellation, or reservation process.",
            },
            {
              question: "Are children or pets welcome?",
              answer:
                "Customize this answer with the policies that apply to your property.",
            },
          ],
        },
        {
          id: "stay-cta",
          type: "cta",
          eyebrow: "Find your dates",
          heading: "Ready to plan your visit?",
          body: "Contact us to check availability and find the right accommodation for your stay.",
          buttons: [
            {
              label: "Check Availability",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "amenities",
      name: "Amenities",
      title: "Amenities",
      slug: "/amenities/",
      sections: [
        {
          id: "amenities-hero",
          type: "hero",
          eyebrow: "Everything you need",
          heading: "Thoughtful amenities for a more comfortable stay.",
          body: "Highlight the features, comforts, conveniences, and special touches guests can enjoy during their visit.",
          images: [
            {
              src: "/studio/assets/stock/images/spacejoy-h2_3dL9yLpU-unsplash.jpg",
              alt: "Hospitality amenities",
              role: "hero",
            },
          ],
        },
        {
          id: "amenities-list",
          type: "services",
          eyebrow: "Included with your stay",
          heading: "Comfort in the details.",
          services: [
            {
              title: "Comfort & Convenience",
              description:
                "Use this card for Wi-Fi, climate control, linens, parking, laundry, kitchen features, or other practical comforts.",
            },
            {
              title: "Relax & Recharge",
              description:
                "Showcase pools, patios, fireplaces, hot tubs, outdoor areas, gathering spaces, or wellness amenities.",
            },
            {
              title: "Guest Services",
              description:
                "Highlight concierge help, local recommendations, housekeeping, check-in support, or other guest services.",
            },
          ],
        },
        {
          id: "amenities-gallery",
          type: "gallery",
          eyebrow: "The experience",
          heading: "Small details make a big difference.",
          images: [
            {
              src: "/studio/assets/stock/images/spacejoy-IH7wPsjwomc-unsplash.jpg",
              alt: "Guest amenity",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
              alt: "Hospitality interior detail",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/spacejoy-YI2YkyaREHk-unsplash.jpg",
              alt: "Comfortable guest amenity",
              role: "card",
            },
          ],
        },
        {
          id: "amenities-cta",
          type: "cta",
          eyebrow: "Questions about your stay?",
          heading: "We're happy to help you plan.",
          buttons: [
            {
              label: "Contact Us",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "area-guide",
      name: "Area Guide",
      title: "Area Guide",
      slug: "/area-guide/",
      sections: [
        {
          id: "area-hero",
          type: "hero",
          eyebrow: "Explore the area",
          heading: "Stay close to the places you came to experience.",
          body: "Use this page to introduce nearby attractions, outdoor activities, restaurants, shopping, events, and local favorites.",
          images: [
            {
              src: "/studio/assets/stock/images/roberto-nickson-rEJxpBskj3Q-unsplash.jpg",
              alt: "Destination and local area",
              role: "hero",
            },
          ],
        },
        {
          id: "area-highlights",
          type: "services",
          eyebrow: "Local favorites",
          heading: "Make the most of your visit.",
          services: [
            {
              title: "Things to Do",
              description:
                "Feature attractions, outdoor recreation, entertainment, tours, activities, or seasonal experiences.",
            },
            {
              title: "Eat & Drink",
              description:
                "Share favorite restaurants, coffee shops, breweries, markets, and other local dining destinations.",
            },
            {
              title: "Explore Nearby",
              description:
                "Introduce downtown areas, neighborhoods, parks, scenic drives, shopping, and easy day trips.",
            },
          ],
        },
        {
          id: "area-cta",
          type: "cta",
          eyebrow: "Plan your getaway",
          heading: "Come stay and explore.",
          body: "Book a comfortable home base and spend your visit discovering everything the area has to offer.",
          buttons: [
            {
              label: "Plan Your Stay",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "gallery",
      name: "Gallery",
      title: "Gallery",
      slug: "/gallery/",
      sections: [
        {
          id: "gallery-hero",
          type: "hero",
          eyebrow: "See your stay",
          heading: "Take a closer look around.",
          body: "Show guests the accommodations, amenities, atmosphere, and details they can expect during their visit.",
          images: [
            {
              src: "/studio/assets/stock/images/spacejoy-YI2YkyaREHk-unsplash.jpg",
              alt: "Hospitality property",
              role: "hero",
            },
          ],
        },
        {
          id: "gallery-images",
          type: "gallery",
          eyebrow: "Property gallery",
          heading: "Picture yourself here.",
          images: [
            {
              src: "/studio/assets/stock/images/spacejoy-9M66C_w_ToM-unsplash.jpg",
              alt: "Guest accommodation",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/spacejoy-IH7wPsjwomc-unsplash.jpg",
              alt: "Guest interior",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/spacejoy-h2_3dL9yLpU-unsplash.jpg",
              alt: "Hospitality interior",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
              alt: "Interior detail",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/spacejoy-YI2YkyaREHk-unsplash.jpg",
              alt: "Relaxing guest space",
              role: "card",
            },
          ],
        },
        {
          id: "gallery-cta",
          type: "cta",
          eyebrow: "Like what you see?",
          heading: "Come experience it for yourself.",
          buttons: [
            {
              label: "Plan Your Stay",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Plan your visit",
          heading: "Let's help you plan your stay.",
          body: "Ask about availability, accommodations, policies, local recommendations, or anything else you need before your visit.",
          images: [
            {
              src: "/studio/assets/stock/images/spacejoy-9M66C_w_ToM-unsplash.jpg",
              alt: "Welcoming hospitality accommodation",
              role: "hero",
            },
          ],
        },
        {
          id: "contact-details",
          type: "contact",
          eyebrow: "Get in touch",
          heading: "Questions before your stay?",
          body: "Update the phone number, email, address, booking information, and contact details in Studio.",
        },
        {
          id: "contact-faq",
          type: "faq",
          eyebrow: "Guest information",
          heading: "A few things guests often ask.",
          faqs: [
            {
              question: "How can I check availability?",
              answer:
                "Contact the property directly or add your preferred booking link here.",
            },
            {
              question: "Where are you located?",
              answer:
                "Add your property address, neighborhood, or general location here.",
            },
            {
              question: "Can you help with local recommendations?",
              answer:
                "Use this answer to explain the local guidance, concierge support, or visitor information you provide.",
            },
          ],
        },
      ],
    },
  ],
};
